import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import FlowerButton from "@/components/FlowerButton";
import { getConsent, saveConsent, COOKIE_SETTINGS_EVENT } from "@/lib/consent";

const Toggle = ({ checked, onChange, disabled, label, desc, testId }) => (
  <label className={`flex items-start gap-3 rounded-2xl bg-[#FAF9F6] border-2 border-dashed border-[#D1D5CB] p-3 ${disabled ? "opacity-80" : "cursor-pointer"}`}>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} className="mt-1 accent-[#78353A] w-4 h-4" data-testid={testId} />
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-[#4A3B32]">{label}{disabled && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#4A3B32]/60">toujours actifs</span>}</span>
      <span className="block text-xs text-[#4A3B32]/75 leading-relaxed">{desc}</span>
    </span>
  </label>
);

export default function CookieBanner() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => !getConsent());
  const [custom, setCustom] = useState(false);
  const [stats, setStats] = useState(() => getConsent()?.stats ?? false);

  useEffect(() => {
    const show = () => { setStats(getConsent()?.stats ?? false); setCustom(true); setOpen(true); };
    window.addEventListener(COOKIE_SETTINGS_EVENT, show);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, show);
  }, []);

  if (!open || pathname.startsWith("/admin")) return null;
  const decide = (value) => { saveConsent(value); setOpen(false); setCustom(false); };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5 pointer-events-none" role="dialog" aria-live="polite" aria-label="Gestion des cookies" data-testid="cookie-banner">
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-[2rem] border-2 border-dashed border-[#D1D5CB] bg-[#FFFDF9]/95 backdrop-blur-md shadow-[0_18px_50px_rgb(74,59,50,0.18)] p-5 sm:p-6 animate-pop-in">
        <div className="flex items-start gap-4">
          <span className="hidden sm:flex w-12 h-12 shrink-0 rounded-full bg-[#FFF2B2] items-center justify-center text-[#78353A]"><Cookie className="w-6 h-6" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-cursive text-3xl text-[#78353A] leading-none">Un petit cookie ? ✿</p>
            <p className="mt-2 text-sm text-[#4A3B32]/85 leading-relaxed">
              MiniMellow utilise uniquement des cookies et données locales <strong>essentiels</strong> (ton panier, tes préférences). Nous n'utilisons aucun traceur publicitaire. Tu peux choisir d'activer ou non la mesure d'audience.{" "}
              <Link to="/confidentialite" className="text-[#78353A] underline decoration-dotted underline-offset-4" data-testid="cookie-policy-link">En savoir plus</Link>
            </p>

            {custom && (
              <div className="mt-4 grid gap-2" data-testid="cookie-settings">
                <Toggle checked disabled label="Essentiels" desc="Panier, bandeau promo, mémorisation de ton choix cookies, paiement sécurisé Stripe. Indispensables au fonctionnement du site." testId="cookie-toggle-essential" />
                <Toggle checked={stats} onChange={setStats} label="Mesure d'audience" desc="Statistiques anonymes de visite pour améliorer la boutique. Aucun outil n'est installé pour le moment : ton choix sera respecté si nous en ajoutons un." testId="cookie-toggle-stats" />
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {custom ? (
                <FlowerButton variant="sage" className="!py-2.5 !px-5 !text-lg" onClick={() => decide(stats)} data-testid="cookie-save-btn">Enregistrer mes choix</FlowerButton>
              ) : (
                <>
                  <FlowerButton variant="pink" className="!py-2.5 !px-5 !text-lg" onClick={() => decide(true)} data-testid="cookie-accept-btn">Tout accepter</FlowerButton>
                  <FlowerButton variant="sage" className="!py-2.5 !px-5 !text-lg" onClick={() => decide(false)} data-testid="cookie-refuse-btn">Tout refuser</FlowerButton>
                  <button type="button" onClick={() => setCustom(true)} className="text-sm text-[#78353A] underline decoration-dotted underline-offset-4" data-testid="cookie-customize-btn">Personnaliser</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
